import {FC, SVGProps, useEffect, useMemo, useState} from 'react';
import { IconTypes } from './Icon-Constants.ts';
import './Icon.scss';

interface IconProps {
    name: string;
    color?: string;
    size?: number;
    className?: string;
    onClick?: () => void;
}

const Icon: FC<IconProps> = ({ name, color, size = 24, className }) => {
    const [IconComponent, setIconComponent] = useState<FC<SVGProps<SVGSVGElement>> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);


    useEffect(() => {
        setLoading(true);
        setError(null);
        const importIcon = async () => {
            try {
                let module;
                switch (name) {
                  case IconTypes.arrowRight:
                    module = await import('@/assets/icons/arrow-right.svg?react');
                    break;
                  case IconTypes.hamburger:
                    module = await import('@/assets/icons/hamburger.svg?react');
                    break;
                  case IconTypes.back:
                    module = await import('@/assets/icons/back.svg?react');
                    break;
                  case IconTypes.close:
                    module = await import('@/assets/icons/close.svg?react');
                    break;
                  case IconTypes.facebook:
                      module = await import('@/assets/icons/Facebook.svg?react');
                      break;
                  case IconTypes.pinterest:
                      module = await import('@/assets/icons/Pinterest.svg?react');
                      break;
                  case IconTypes.doubleArrowUp:
                  case IconTypes.doubleArrowLeft:
                  case IconTypes.doubleArrowRight:
                      module = await import('@/assets/icons/double-arrow-up.svg?react');
                      break;
                  case IconTypes.play:
                    module = await import('@/assets/icons/play.svg?react');
                    break;
                  case IconTypes.pause:
                    module = await import('@/assets/icons/pause.svg?react');
                    break;
                  case IconTypes.refresh:
                    module = await import('@/assets/icons/Refresh.svg?react');
                    break;
                  default:
                    throw new Error(`Icon "${name}" not found`);
                }
                setIconComponent(() => {return module.default});

            } catch (err) {
                if (err instanceof Error) {
                  setError(err);
                } else {
                  setError(new Error("Failed to load icon"));
                }
            } finally {
                setLoading(false);
            }
        };

        importIcon();
    }, [name]);

    const classList = useMemo(() => {
      return [
          'fm-icon',
          className,
          `fm-icon--${name}`
        ].join(' ');
    }, [className, name]);

    if (loading) {
        return <svg width={size} height={size} className={className}><circle cx={size/2} cy={size/2} r={size/3} fill="currentColor" /></svg>; 
    }

    if (error) {
        console.error(`Error loading icon ${name}:`, error);
        return <svg width={size} height={size} className={className}><path d="M12 2L2 22h20L12 2zm1 17h-2v-6h2v6zm-1-8h1v-2h-1v2z" fill="currentColor"/></svg>;
    }
    if (!IconComponent) {
        return null;
    }

    const style: React.CSSProperties = {
        fill: color,
        width: size,
        height: size,
    };

    return (
      <IconComponent
        fill={color}
        style={style}
        className={classList}
        data-testid="test-fm-icon"
      />
    );
};

export default Icon;
